import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { GeneralDialog, GeneralTabs } from 'sharedComponents';
import { IconInfoFilled } from 'svgIcons/MotionPortalIcons';
import { Container } from 'react-bootstrap';

import { CabinetDrive } from './CabinetDrive';
import { SingleDrive } from './SingleDrive';
import { defaultDriveTab, driveTabTitle } from './constants';
import { getInitialSuffix } from './helpers';
import colors from 'theme/_colors.scss';
import './style.scss';

const SerialNumberEditorComponent = ({
    model,
    actions,
    ...props
}) => {
    const { t: translate } = useTranslation();
    const { isModalOpen, onClose, onApply, selectedAssetDetails, gatewayID } = props;

    const initialSuffixWithLabel = getInitialSuffix(selectedAssetDetails);
    const [singleDriveValidation, setSingleDriveValidation] = useState(false);
    const [cabinetDriveValidation, setCabinetDriveValidation] = useState(false);
    const [currentSerialNumber, setCurrentSerialNumber] = useState(selectedAssetDetails?.serialNumber || '');
    const [currentSuffix, setCurrentSuffix] = useState(initialSuffixWithLabel);

    const isValidSerialNumber = model?.validateSerialNumber?.isValid;
    const hasValidChanges = singleDriveValidation && isValidSerialNumber || cabinetDriveValidation && isValidSerialNumber;

    useEffect(() => {
        return () => {
            actions.clearValidationState();
        };
    }, []);

    const handleSingleDriveValidation = (isValid) => {
        setSingleDriveValidation(isValid);
    };

    const handleCabinetDriveValidation = (isValid) => {
        setCabinetDriveValidation(isValid);
    };

    const handleSerialNumberChange = (serialNumber) => {
        setCurrentSerialNumber(serialNumber);
    };

    const handleSuffixChange = (suffix) => {
        setCurrentSuffix(suffix);
    };

    const handleClose = () => {
        // Reset validation states when modal closes
        setSingleDriveValidation(false);
        setCabinetDriveValidation(false);
        setCurrentSerialNumber('');
        setCurrentSuffix(null);


        actions.clearValidationState();
        onClose();
    };

    const handleApply = () => {
        let serialNumber = currentSerialNumber || selectedAssetDetails?.serialNumber || '';
        const currentSuffixName = currentSuffix?.name ? currentSuffix?.name : initialSuffixWithLabel?.name;
        if (cabinetDriveValidation && currentSuffixName) {
            serialNumber = `${currentSerialNumber}_${currentSuffixName}`;
        }

        const isSingleDrive = singleDriveValidation;
        const isCabinetDrive = cabinetDriveValidation;

        const applySerialNumberData = {
            currentSerialNumber: serialNumber,
            currentSuffix: currentSuffixName,
            originalSerialNumber: currentSerialNumber || selectedAssetDetails?.serialNumber || '', // The serial number without suffix
            suffix: currentSuffixName,
            IsCabinetDriveValid: cabinetDriveValidation,
            IsSingleDriveValid: singleDriveValidation,
            isMultiModuleDrive: isSingleDrive ? false : isCabinetDrive ? true : !!selectedAssetDetails?.isMultiModuleDrive,
        };

        const requestBody = {
            assetId: selectedAssetDetails.assetId,
            serialNumber,
            cabinetSerialNumber: isSingleDrive ? '' : currentSerialNumber,
            suffix: isSingleDrive ? null : currentSuffixName,
            isMultiModuleDrive: isSingleDrive ? false : isCabinetDrive ? true : !!selectedAssetDetails?.isMultiModuleDrive,
            isApproved: true,
            gatewayID
        };
        actions.assignSerialNumber(requestBody);

        onApply(applySerialNumberData);

        handleClose();
    };

    return (
        <GeneralDialog
            show={isModalOpen}
            close={handleClose}
            title={<span><strong>{translate(`${selectedAssetDetails?.isSyntheticSerialNumber ? 'ABB.Powertrain.Frontend.assignSerialNumberTitle' : 'ABB.Powertrain.Frontend.editSerialNumberTitle'}`)}</strong></span>}
            closeButton={true}
            dialogClassName='assign__serialnumber'
            cancelButtonProps={{
                text: translate('ABB.Powertrain.Frontend.cancelLabel')
            }}
            acceptButtonProps={{
                text: translate('ABB.Powertrain.Frontend.apply'),
                disabled: !hasValidChanges,
                onClick: handleApply,
            }}
        >
            <Container fluid className='gx-0'>
                <div className='assign__serialnumber__modal'>
                    <div className='assign__serialnumber__toast'>
                        <span className='icon'><IconInfoFilled width={'14px'} height={'14px'} color={colors.dodgerBlue} /></span>
                        <span className='title'>
                            {translate(`${selectedAssetDetails?.isSyntheticSerialNumber ? 'ABB.Powertrain.Frontend.assignSerialNumberInfoText' : 'ABB.Powertrain.Frontend.editSerialNumberInfoText'}`)}
                        </span>
                    </div>
                    <div className='header__tab-wrapper'>
                        <GeneralTabs
                            options={{
                                singleDrive: {
                                    title: driveTabTitle.singleDrive,
                                    component: <SingleDrive
                                        initialSerialNumber={selectedAssetDetails?.serialNumber}
                                        originalSerialNumber={selectedAssetDetails?.originalSerialNumber}
                                        onValidationStateChange={handleSingleDriveValidation}
                                        onSerialNumberChange={handleSerialNumberChange}
                                        model={model}
                                        actions={actions}
                                    />
                                },
                                cabinetDrive: {
                                    title: driveTabTitle.cabinetDrive,
                                    component: <CabinetDrive
                                        cabinetSerialNumber={selectedAssetDetails?.cabinetSerialNumber}
                                        originalSerialNumber={selectedAssetDetails?.originalSerialNumber}
                                        initialSuffix={initialSuffixWithLabel}
                                        suffixes={selectedAssetDetails?.validSuffixes || []}
                                        isSyntheticSerialNumber={selectedAssetDetails?.isSyntheticSerialNumber}
                                        onValidationStateChange={handleCabinetDriveValidation}
                                        onSerialNumberChange={handleSerialNumberChange}
                                        onSuffixChange={handleSuffixChange}
                                        model={model}
                                        actions={actions}
                                    />
                                }
                            }}
                            defaultTab={selectedAssetDetails?.isMultiModuleDrive ? defaultDriveTab.cabinetDrive : defaultDriveTab.singleDrive}
                        />
                    </div>
                </div>
            </Container>
        </GeneralDialog>
    );
};

SerialNumberEditorComponent.propTypes = {
    isModalOpen: PropTypes.bool,
    onClose: PropTypes.func,
    selectedAssetDetails: PropTypes.shape({
        serialNumber: PropTypes.string,
        cabinetSerialNumber: PropTypes.string,
        isSyntheticSerialNumber: PropTypes.bool,
        isMultiModuleDrive: PropTypes.bool,
        suffix: PropTypes.string,
        validSuffixes: PropTypes.array,
        originalSerialNumber: PropTypes.string
    }),
    onApply: PropTypes.func,
    gatewayID: PropTypes.number
};

export default SerialNumberEditorComponent;
